/**
 * "Clinical Twin": what the engine actually knows.
 *
 * The client's sidebar has a Clinical Twin entry and no visual for what it
 * opens. The comparison itself belongs on the treatment panel, where the
 * decision is made, so putting a second copy of it here would be a screen
 * nobody would use twice.
 *
 * What is genuinely missing is this. On the day MCOS goes live there are no
 * outcome records, so almost every combination will correctly show "Limited
 * MCOS data" and every ring on the treatment panel will be empty. A clinician
 * who cannot see why will conclude the feature is broken. This screen is the
 * answer to "why is the ring empty": the size of the dataset, the thresholds
 * in force, and which combinations have enough behind them.
 *
 * Two things it says out loud, because both are counter-intuitive:
 *
 *   - The dataset is cross-clinic. A cohort is drawn from de-identified MCOS
 *     outcomes, not from this clinic's own patients, which is the entire
 *     reason the comparison is worth anything.
 *   - Records are not cohort size. For any given patient the cohort is
 *     smaller: matching, hard exclusions and the similarity threshold all cut
 *     into it.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  Card,
  EmptyState,
  ErrorState,
  InfoHint,
  LoadingRows,
  PageHeader,
  Pill,
  Table,
  Td,
  Th,
} from '../components/ui';

interface Dataset {
  total: number;
  bySource: Record<string, number>;
  thresholds: { clinician: number; patient: number };
  combinations: Array<{
    combinationKey: string;
    names: string[];
    records: number;
    meetsClinicianMinimum: boolean;
    meetsPatientMinimum: boolean;
  }>;
}

export function ClinicalTwinPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['twin-dataset'],
    queryFn: () => api.get<Dataset>('/twin/dataset'),
  });

  if (error) return <ErrorState error={error} retry={() => void refetch()} />;
  if (isLoading || !data) return <LoadingRows rows={8} />;

  const real = data.total - (data.bySource.DEMO ?? 0);

  return (
    <>
      <PageHeader
        title="Clinical Twin"
        subtitle="What the engine has to compare a patient against, and the rules it compares under."
      />

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-xs text-ink-muted">Outcome records</p>
          <p className="mt-0.5 font-display text-3xl font-bold leading-none text-ink">{real}</p>
          <p className="mt-1.5 text-xs text-ink-muted">
            De-identified patient outcomes across MCOS, not only this clinic.
          </p>
          {/* Demo rows counted apart and never folded into the real total: a
              number a clinician reads as evidence must be evidence. */}
          {(data.bySource.DEMO ?? 0) > 0 && (
            <p className="mt-1 text-xs text-ink-muted">
              Plus {data.bySource.DEMO} demonstration records, excluded on production.
            </p>
          )}
        </Card>

        <Card>
          <p className="text-xs text-ink-muted">
            Minimum cohort, clinician{' '}
            <InfoHint label="Why two numbers">
              A clinician is trained to weigh a small group. A patient reads the same figure as a
              promise about themselves, so a patient sees a result only from a larger group of
              matched patients than a clinician does.
            </InfoHint>
          </p>
          <p className="mt-0.5 font-display text-3xl font-bold leading-none text-ink">
            {data.thresholds.clinician}
          </p>
          <p className="mt-1.5 text-xs text-ink-muted">
            Below this, the treatment panel shows no percentage at all.
          </p>
        </Card>

        <Card>
          <p className="text-xs text-ink-muted">Minimum cohort, patient facing</p>
          <p className="mt-0.5 font-display text-3xl font-bold leading-none text-ink">
            {data.thresholds.patient}
          </p>
          <p className="mt-1.5 text-xs text-ink-muted">
            The higher bar applied to anything a patient reads.
          </p>
        </Card>
      </div>

      <Card
        title="Combinations on record"
        subtitle="Records held per exact combination. A patient's own cohort is always smaller: matching, exclusions and the similarity threshold all reduce it."
        padded={false}
      >
        {data.combinations.length === 0 ? (
          <EmptyState
            title="No outcomes recorded yet"
            body="A record is written when a patient reaches the end of a six month window, or when a plan is stopped. Until then every combination reads Limited MCOS data, which is correct rather than broken."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Combination</Th>
                <Th align="right">Records</Th>
                <Th align="right">Clinician panel</Th>
                <Th align="right">Patient facing</Th>
              </tr>
            </thead>
            <tbody>
              {data.combinations.map((row) => (
                <tr key={row.combinationKey} className="hover:bg-ink/[0.02]">
                  <Td>
                    <span className="font-semibold text-ink">{row.names.join(' + ')}</span>
                    <span className="block text-xs text-ink-muted">{row.combinationKey}</span>
                  </Td>
                  <Td align="right" className="font-semibold text-ink">
                    {row.records}
                  </Td>
                  <Td align="right">
                    <Pill
                      className={
                        row.meetsClinicianMinimum
                          ? 'bg-[var(--sage-soft)] text-sage-deep'
                          : 'bg-ink/5 text-ink-muted'
                      }
                    >
                      {row.meetsClinicianMinimum ? 'Can show a rate' : 'Limited data'}
                    </Pill>
                  </Td>
                  <Td align="right">
                    <Pill
                      className={
                        row.meetsPatientMinimum
                          ? 'bg-[var(--sage-soft)] text-sage-deep'
                          : 'bg-ink/5 text-ink-muted'
                      }
                    >
                      {row.meetsPatientMinimum ? 'Can show a rate' : 'Limited data'}
                    </Pill>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card title="How a cohort is built" className="mt-6">
        <ol className="space-y-2 text-sm text-ink-muted">
          <li>
            <span className="font-semibold text-ink">1. Exact combination.</span> Tirzepatide alone
            and Tirzepatide plus B12 are different exposures, so they are different cohorts. A query
            for one never includes the other.
          </li>
          <li>
            <span className="font-semibold text-ink">2. Hard exclusions.</span> Paediatric, type 1
            diabetes, pregnancy, corrupt, duplicate and invalid chronology records are removed
            outright, as is a phenotype mismatch.
          </li>
          <li>
            <span className="font-semibold text-ink">3. Similarity threshold.</span> What remains is
            scored against the patient across age, sex, BMI, glycaemic state, lipids and phenotype,
            and anything below the library&apos;s threshold is dropped.
          </li>
          <li>
            <span className="font-semibold text-ink">4. Minimum cohort.</span> What survives is
            counted. Below the minimum the answer is the sentence &quot;Limited MCOS data&quot; and
            no number, never a smaller number.
          </li>
        </ol>
      </Card>
    </>
  );
}
